<template>
  <div>
    <!-- Name -->
    <div class="row">
      <div class="col-6">
        <div class="label mt-2 mb-1 container-with-required-warn">
          <div>Name / Defect</div>
          <i
            class="far fa-info-circle required-warn"
            v-if="!occurrence.name"
            :title="publicConstants.missingOccurrenceData"
            v-b-tooltip:hover
          />
        </div>
        <input type="text" class="form-control" v-model="occurrence.name" />
      </div>
      <div class="col-6">
        <div class="label mt-2 mb-1 container-with-required-warn">
          <div>Type</div>
          <i
            class="far fa-info-circle required-warn"
            v-if="!occurrence.technicalOccurrenceTypeId"
            :title="publicConstants.missingOccurrenceData"
            v-b-tooltip:hover
          />
        </div>
        <v-select
          :options="soTypes"
          label="name"
          :clearable="false"
          :reduce="(t) => t.id"
          placeholder="Please select a type..."
          v-model="occurrence.technicalOccurrenceTypeId"
          :disabled="occurrence.isCompleted || !userCanEdit"
        ></v-select>
      </div>
    </div>

    <!-- Aircraft && Airport -->
    <div class="row">
      <div class="col-6">
        <div class="label mt-2 mb-1 container-with-required-warn">
          <div>Aircraft</div>
          <i
            class="far fa-info-circle required-warn"
            v-if="!occurrence.aircraft"
            :title="publicConstants.missingOccurrenceData"
            v-b-tooltip:hover
          />
        </div>
        <v-select
          :options="aircrafts"
          label="registration"
          :clearable="false"
          :reduce="(aircraft) => aircraft.id"
          placeholder="Please select an Aircraft..."
          v-model="occurrence.aircraft"
          :disabled="occurrence.isCompleted || !userCanEdit"
          @input="aircraftSelected"
        ></v-select>

        <!-- Hours & Cycles -->
        <div class="mt-2 mb-2 p-1">
          <div class="row">
            <div class="col-4">
              <div class="label">
                Hours & Cycles
                <i class="far fa-info-circle" title="Live data" v-b-tooltip:hover></i>
              </div>

              <div class="row mb-2">
                <div class="col-4">Hours</div>
                <div class="col-8">
                  {{ aircraftInfo.hours || 'N/A' }}
                </div>
              </div>

              <div class="row">
                <div class="col-4">Cycles</div>
                <div class="col-8">
                  {{ aircraftInfo.cycles || 'N/A' }}
                </div>
              </div>
            </div>
            <div class="col-4">
              <div class="label">
                Prev Flight
                <i class="far fa-info-circle" title="Live data" v-b-tooltip:hover></i>
              </div>

              <div class="row mb-2">
                <div class="col-12">
                  {{ aircraftInfo.previousFlightDescription || 'N/A' }}
                </div>
              </div>
              <div class="row mb-2" v-if="aircraftInfo.previousFlightAta">
                <div class="col-12">
                  <span class="badge badge-secondary">ATA</span>
                  {{ aircraftInfo.previousFlightAta | formatDate }}
                  <i
                    v-if="aircraftInfo.previousFlightAta"
                    class="far fa-lightbulb cursor-pointer"
                    title="Use as Ground Time ATA"
                    v-b-tooltip:hover
                    @click="useAsAta(aircraftInfo.previousFlightAta)"
                  ></i>
                </div>
              </div>
            </div>

            <div class="col-4">
              <div class="label">
                Next SKD Flight
                <i class="far fa-info-circle" title="Live data" v-b-tooltip:hover></i>
              </div>

              <div class="row mb-2">
                <div class="col-12">
                  {{ aircraftInfo.nextFlightDescription || 'N/A' }}
                </div>
              </div>
              <div class="row mb-2" v-if="aircraftInfo.nextFlightStd">
                <div class="col-12">
                  <span class="badge badge-secondary">STD</span>
                  {{ aircraftInfo.nextFlightStd | formatDate }}
                  <i
                    v-if="aircraftInfo.previousFlightAta"
                    class="far fa-lightbulb cursor-pointer"
                    title="Use as STD"
                    v-b-tooltip:hover
                    @click="useAsStd(aircraftInfo.nextFlightStd)"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-6">
        <div class="label mt-2 mb-1 container-with-required-warn">
          <div>Airport</div>
          <i
            class="far fa-info-circle required-warn"
            v-if="!occurrence.airport"
            :title="publicConstants.missingOccurrenceData"
            v-b-tooltip:hover
          />
        </div>
        <v-select
          :options="airports"
          label="iataCode"
          :clearable="false"
          :reduce="(airport) => airport.id"
          placeholder="Please select an Airport..."
          v-model="occurrence.airport"
          :disabled="occurrence.isCompleted || !userCanEdit"
        ></v-select>

        <div class="mt-2 mb-2 p-1">
          <div class="label">
            Station info
            <a :href="stationInfo.sisPdfUrl" target="_blank">
              <i class="far fa-link"></i>
              SIS
            </a>
          </div>

          <div class="row mb-2">
            <div class="col-2">MRO</div>
            <div class="col-10">
              {{ stationInfo.mro || 'N/A' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-6">
        <div class="label mb-1">MX SUPPORT ON SITE</div>
        <v-select
          :options="maintenanceCompanies"
          label="name"
          :clearable="false"
          :reduce="(company) => company.id"
          placeholder="Please select a maintenance company..."
          v-model="occurrence.maintenanceCompanyId"
          :disabled="occurrence.isCompleted || !userCanEdit"
        ></v-select>
      </div>
      <div class="col-6 d-flex justify-content-start align-items-center pt-4">
        <div>
          <checkbox-with-label :label="'Engineering Support'" v-model="occurrence.isEngineeringSupport"></checkbox-with-label>
        </div>
        <div class="ml-4">
          <checkbox-with-label :label="'Hangar / Facility'" v-model="occurrence.isHangar"></checkbox-with-label>
        </div>
      </div>
    </div>

    <div class="row mt-2" v-if="isFreeCompanySelected">
      <div class="col-12">
        <textarea-autosize
          class="form-control comment-textarea"
          rows="4"
          placeholder="Enter address, contact information, email address…"
          v-model="occurrence.maintenanceCompanyInfo"
          :min-height="50"
        ></textarea-autosize>

        <div class="mt-2">
          Please attach
          <a download="MF008_Checklist_ADHOC_Vergabe.docx" href="/MF008_Checklist_ADHOC_Vergabe.docx">Form MF008</a>
          to this Technical Occurrence

          <i
            class="far fa-info-circle"
            title="If MX is required on a station that is not on the ASL (Approved Supplier List) -> Processing of ADHOC MX Selection Check List (Form M-F.008) ->  Check List must be checked and filled in before the MX event"
            v-b-tooltip.hover
          ></i>
        </div>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-4">
        <div class="label mt-2 mb-1 container-with-required-warn">
          <div>Ground time ata</div>

          <i
            class="far fa-info-circle required-warn"
            v-if="!occurrence.groundTimeAta"
            :title="publicConstants.missingOccurrenceData"
            v-b-tooltip:hover
          />
        </div>
        <board-item-date-picker
          :disabled="occurrence.isCompleted || !userCanEdit"
          v-model="occurrence.groundTimeAta"
          :hideDateClearBtn="true"
        ></board-item-date-picker>
      </div>
      <div class="col-4">
        <div class="label mt-2 mb-1 container-with-required-warn">
          <div>STD</div>

          <i
            class="far fa-info-circle required-warn"
            v-if="!occurrence.scheduledTimeOfDeparture"
            :title="publicConstants.missingOccurrenceData"
            v-b-tooltip:hover
          />
        </div>
        <board-item-date-picker
          :disabled="occurrence.isCompleted || !userCanEdit"
          v-model="occurrence.scheduledTimeOfDeparture"
        ></board-item-date-picker>
      </div>
      <div class="col-4">
        <div class="label mt-2 mb-1 d-flex justify-content-between align-items-center">
          <div>Etd</div>
        </div>

        <board-item-date-picker
          :disabled="occurrence.isCompleted || !userCanEdit"
          v-model="occurrence.estimatedTimeOfDeparture"
        ></board-item-date-picker>
      </div>
    </div>

    <div>
      <div class="label mt-2 mb-1 container-with-required-warn">
        <div>Defect description</div>
        <i
          class="far fa-info-circle required-warn"
          v-if="!occurrence.generalRemarks"
          :title="publicConstants.missingOccurrenceData"
          v-b-tooltip:hover
        />
      </div>
      <textarea-autosize
        class="form-control comment-textarea"
        style="padding-right: 30px"
        rows="3"
        placeholder=""
        v-model="occurrence.generalRemarks"
        :min-height="75"
        :disabled="occurrence.isCompleted || !userCanEdit"
      ></textarea-autosize>
    </div>

    <div v-if="occurrence.flights && occurrence.flights.length">
      <div class="label pt-3">Selected Flights</div>

      <table class="aog-table table table-striped">
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">Flight</th>
            <th scope="col">Operator</th>
            <th scope="col">Type</th>
            <th scope="col">Registration</th>
            <th scope="col">Departure</th>
            <th scope="col">Arrival</th>
            <th scope="col" class="col-date">STD</th>
            <th scope="col" class="col-date">STA</th>
            <th scope="col">State</th>
            <th scope="col" class="reason">Selected Reason</th>
            <th scope="col" class="reason-comment">Selected Reason Comment</th>
            <th scope="col" class="remove-col"></th>
          </tr>
        </thead>
        <tbody>
          <tr class="" v-for="(flight, index) in occurrence.flights" :key="flight.id + index">
            <td>{{ flight.date | formatDateWithoutHours }}</td>
            <td>{{ flight.flightNo }}</td>
            <td>{{ flight.operator }}</td>
            <td>{{ flight.aircraftType }}</td>

            <td>{{ flight.aircraftRegistration }}</td>

            <td>{{ flight.departureAirport }}</td>
            <td>{{ flight.arrivalAirport }}</td>

            <td>{{ flight.scheduledTimeOfDeparture | formatTime }}</td>
            <td>{{ flight.scheduledTimeOfArrival | formatTime }}</td>

            <td>{{ flight.flightState }}</td>
            <td class="reason">
              <v-select
                :options="selectedReasons"
                label="type"
                :clearable="false"
                :reduce="(r) => r"
                placeholder="Select a reason..."
                v-model="flight.selectedReason"
                :disabled="occurrence.isCompleted || !userCanEdit"
              ></v-select>
            </td>
            <td>
              <input type="text" class="form-control" v-model="flight.selectedReasonComment" />
            </td>
            <td>
              <i
                v-if="!occurrence.isCompleted && userCanEdit"
                class="so-flight-remove far fa-trash-alt cursor-pointer"
                v-on:click="removeFlight(flight)"
              ></i>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-2 justify-content-between d-flex" v-if="!occurrence.isCompleted">
      <div>
        <div v-b-tooltip.hover.right :title="errorTitle">
          <button
            type="button"
            :disabled="modelInvalid || addFlightsLoading"
            class="btn btn-block btn-sm btn-success mt-2 mb-2 d-flex align-items-center"
            @click="addAffectedFlights()"
          >
            <i class="far fa-plus mr-2" v-if="!addFlightsLoading"></i>
            <i class="far fa-spinner fa-spin mr-2" v-else></i>
            Add affected Flight
          </button>
        </div>
      </div>

      <div>
        <button class="btn btn-block btn-sm btn-success mt-2 mb-3" @click="saveChanges()">
          <i class="far fa-save"></i>
          Save Changes
        </button>
      </div>
    </div>

    <b-modal
      ref="addFlights"
      id="addFlights"
      body-class="modal-body-wo"
      hide-footer
      hide-header
      size="wide"
      title="Add Affected Flight"
      :no-enforce-focus="true"
    >
      <TechnicalOccurrenceFlightsSelector
        :registration="occurrence.aircraft"
        :date="occurrence.groundTimeAta"
        @saveFlights="saveFlights"
        @closeModal="closeModal"
      ></TechnicalOccurrenceFlightsSelector>
    </b-modal>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { SpecialOccurrenceModule, SpecialOccurrenceStore } from '@/store/specialOccurrence';
  import {
    AircraftDto,
    AirportDto,
    SpecialOccurrenceAccessLevel,
    SpecialOccurrenceFlightInsertDto,
    TechnicalOccurrenceDto,
  } from '@/apiclient/apiclient_generated';
  import { Prop } from 'vue-property-decorator';
  import { GlobalHelper } from '@/helpers/global-helper';
  import TechnicalOccurrenceFlightsSelector from '@/components/SpecialOccurrence/Data/Flights/TechnicalOccurrenceFlightsSelector.vue';
  import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';
  import PublicConstants from '@/types/publicConstants';

  @Component({
    components: { LoadingSpinner, TechnicalOccurrenceFlightsSelector },
  })
  export default class TechnicalOccurrenceData extends Vue {
    private store: SpecialOccurrenceModule = SpecialOccurrenceStore;
    @Prop() occurrence: TechnicalOccurrenceDto;

    addFlightsLoading = false;
    publicConstants = PublicConstants;
    async mounted() {
      await this.store.loadMaintenanceCompanies();
      await this.store.loadTechnicalOccurrenceTypes();
      await this.store.loadSelectedFlightReasons();

      this.$forceUpdate();
    }

    get stationInfo(): AirportDto {
      return this.store.airports.find((_) => _.id == this.occurrence.airport) || new AirportDto();
    }

    get aircraftInfo(): AircraftDto {
      return this.store.aircrafts.find((_) => _.id == this.occurrence.aircraft) || new AircraftDto();
    }

    get airports() {
      return this.store.airports;
    }

    get aircrafts() {
      return this.store.aircrafts;
    }

    get soTypes() {
      return this.store.technicalOccurrenceTypes;
    }

    get maintenanceCompanies() {
      return this.lodash.orderBy(this.store.maintenanceCompanies, 'name');
    }

    get isFreeCompanySelected() {
      return this.store.maintenanceCompanies.some((_) => _.id === this.occurrence.maintenanceCompanyId && _.name.toLowerCase() === 'free');
    }

    get selectedReasons() {
      return this.store.selectedFlightReasons.map((_) => _.name);
    }

    get errorTitle(): string {
      if (this.modelInvalid) {
        return 'Please select Aircraft and Start date';
      }
      return '';
    }

    get modelInvalid(): boolean {
      const occ = this.occurrence as TechnicalOccurrenceDto;
      return !occ.aircraft || !occ.groundTimeAta;
    }

    get userCanEdit(): boolean {
      return (
        this.store.currentOccurrenceUserAccessLevel == SpecialOccurrenceAccessLevel.Admin ||
        this.store.currentOccurrenceUserAccessLevel == SpecialOccurrenceAccessLevel.Editor
      );
    }

    aircraftSelected() {
      const airportId = this.aircraftInfo.previousFlightArrAirportId;
      const airport = this.airports.find((_) => _.id == airportId.toString());

      if (airport && !this.occurrence.airport) {
        this.occurrence.airport = airport.id.toString();
      }
    }

    useAsStd(std) {
      this.occurrence.scheduledTimeOfDeparture = std;
    }

    useAsAta(ata) {
      this.occurrence.groundTimeAta = ata;
    }

    async saveChanges() {
      await this.store.saveOccurrenceDataChanges(this.occurrence);
      GlobalHelper.NotifySuccess('Special Occurrence updated');

      await this.store.loadData();
    }

    async saveFlights(flights) {
      this.store.currentOccurrence.flights = [];
      this.store.currentOccurrence.flights = [...flights];

      const dto = {
        occurrenceId: this.occurrence.id,
        flights: this.store.currentOccurrence.flights,
      } as SpecialOccurrenceFlightInsertDto;

      await this.store.saveOccurrenceFlights(dto);

      GlobalHelper.NotifySuccess('Special Occurrence flights updated');
    }

    async addAffectedFlights() {
      this.addFlightsLoading = true;

      await this.store.saveOccurrenceDataChanges(this.occurrence);
      await this.store.getAogFlights((this.occurrence as TechnicalOccurrenceDto).groundTimeAta);

      this.addFlightsLoading = false;

      const modal: any = this.$refs['addFlights'];
      modal.show();
    }

    closeModal() {
      const modal: any = this.$refs['addFlights'];
      modal.hide();
    }

    async removeFlight(flight) {
      const index = this.occurrence.flights.findIndex((_) => _.id === flight.id);
      this.occurrence.flights.splice(index, 1);
      await this.saveFlights(this.occurrence.flights);
    }
  }
</script>

<style lang="scss">
  .k-calendar-container {
    z-index: 1050 !important;
  }
</style>
